import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { InboxIcon, BellIcon, CheckCircleIcon, ArchiveBoxIcon, TrashIcon, ExclamationTriangleIcon, CalendarIcon, ClockIcon, ListTodoIcon, XIcon } from './ui/icons';
import fcmService from '../services/fcmService';
import PushNotificationSettings from './PushNotificationSettings';

const NOTIF_STATUS_COLORS = {
  unread: 'bg-blue-100 text-blue-700',
  read: 'bg-slate-100 text-slate-500',
  archived: 'bg-slate-50 text-slate-400',
};

// Popup notification component that must be manually closed
function NotificationPopup({
  notification,
  onClose,
  onView
}: {
  notification: any;
  onClose: () => void;
  onView: () => void;
}) {
  const getPopupIcon = (type: string) => {
    switch (type) {
      case 'meeting_reminder':
        return <CalendarIcon className="w-8 h-8 text-purple-500" />;
      case 'task_due_soon':
        return <ListTodoIcon className="w-8 h-8 text-blue-500" />;
      case 'task_overdue':
        return <ClockIcon className="w-8 h-8 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-8 h-8 text-yellow-500" />;
      case 'success':
        return <CheckCircleIcon className="w-8 h-8 text-green-500" />;
      default:
        return <BellIcon className="w-8 h-8 text-primary-500" />;
    }
  };

  const getPopupBorderColor = (type: string) => {
    switch (type) {
      case 'meeting_reminder':
        return 'border-l-purple-500';
      case 'task_due_soon':
        return 'border-l-blue-500';
      case 'task_overdue':
        return 'border-l-red-500';
      case 'warning':
        return 'border-l-yellow-500';
      case 'success':
        return 'border-l-green-500';
      default:
        return 'border-l-primary-500';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 animate-fadeIn">
      <div className={`bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 border-l-4 ${getPopupBorderColor(notification.type)} animate-scaleIn`}>
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-50 rounded-lg">
              {getPopupIcon(notification.type)}
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-800">{notification.title}</h3>
              <p className="text-xs text-slate-400">Just now</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-slate-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-slate-400 hover:text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-slate-600 whitespace-pre-line leading-relaxed">
            {notification.message}
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 bg-slate-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition font-medium"
          >
            Dismiss
          </button>
          {notification.link && (
            <button
              onClick={onView}
              className="flex-1 px-4 py-2.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition font-medium"
            >
              View Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(date: string) {
  const now = new Date();
  const d = new Date(date);
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function getNotifIcon(type: string) {
  switch (type) {
    case 'email':
      return <InboxIcon className="w-4 h-4 text-primary-600" />;
    case 'info':
      return <span className="inline-block w-2 h-2 rounded-full bg-blue-400 mt-1 mr-2 align-middle" />;
    case 'success':
      return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
    case 'warning':
      return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
    case 'meeting_reminder':
      return <CalendarIcon className="w-4 h-4 text-purple-600" />;
    case 'task_due_soon':
      return <ListTodoIcon className="w-4 h-4 text-blue-600" />;
    case 'task_overdue':
      return <ClockIcon className="w-4 h-4 text-red-600" />;
    case 'task':
    case 'reminder':
      return <ListTodoIcon className="w-4 h-4 text-indigo-600" />;
    default:
      return <span className="inline-block w-2 h-2 rounded-full bg-slate-400 mt-1 mr-2 align-middle" />;
  }
}

export default function NotificationsMenu({ userId, isActive = true }: { userId: string; isActive?: boolean }) {
  const [open, setOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [allNotifications, setAllNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read' | 'archived'>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [fcmInitialized, setFcmInitialized] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [popupQueue, setPopupQueue] = useState<any[]>([]); // Queue of popup notifications
  const ref = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const fcmUnsubscribeRef = useRef<(() => void) | null>(null);
  const fcmInitializingRef = useRef(false); // Track if initialization is in progress
  const fcmInitializedRef = useRef(false); // Track if already initialized this session

  // Handle closing the current popup
  const handleClosePopup = () => {
    setPopupQueue((prev) => prev.slice(1)); // Remove first item from queue
  };

  // Handle viewing the notification from popup
  const handleViewFromPopup = (notification: any) => {
    if (notification.link) {
      window.location.href = notification.link;
    }
    handleClosePopup();
  };

  useEffect(() => {
    if (!isActive) return;
    if (userId) fetchNotifications();
    // eslint-disable-next-line
  }, [userId, isActive]);

  useEffect(() => {
    if (!isActive) return;
    if (open && notifications.length === 0 && !loading) fetchNotifications();
    // eslint-disable-next-line
  }, [open, isActive]);

  useEffect(() => {
    if (!isActive) return;
    if (sidebarOpen) fetchAllNotifications();
    // eslint-disable-next-line
  }, [sidebarOpen, filter, page, isActive]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) setSidebarOpen(false);
    }
    if (sidebarOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sidebarOpen]);

  // Initialize FCM push notifications - ONCE per session
  useEffect(() => {
    if (!isActive) return;
    const initializeFCM = async () => {
      try {
        // Strong guards to prevent multiple initializations
        if (!userId) {
          console.log('❌ No userId, skipping FCM initialization');
          return;
        }

        if (fcmInitializingRef.current) {
          console.log('⏳ FCM initialization already in progress, skipping');
          return;
        }

        if (fcmInitializedRef.current) {
          console.log('✅ FCM already initialized this session, skipping');
          return;
        }

        // Check session storage as backup
        const sessionKey = 'fcm_initialized_' + userId;
        if (sessionStorage.getItem(sessionKey)) {
          console.log('💾 FCM session found, marking as initialized');
          fcmInitializedRef.current = true;
          setFcmInitialized(true);
          return;
        }

        if (!fcmService.isSupported()) {
          console.log('🚫 Push notifications not supported on this device');
          return;
        }

        // Set initialization in progress
        fcmInitializingRef.current = true;
        console.log('🚀 Starting FCM initialization for user:', userId);

        // Subscribe to push notifications
        const token = await fcmService.subscribeToPushNotifications();
        if (token) {
          setPushNotificationsEnabled(true);
          console.log('✅ Push notifications enabled successfully');
        }

        // Set up foreground message listener (only once)
        console.log('🎧 Setting up FCM message listener...');
        const unsubscribe = fcmService.onMessageReceived((payload) => {
          console.log('📨 Foreground message received:', payload);

          // Only show notification if page is actually visible (foreground)
          if (document.visibilityState === 'visible') {
            console.log('📺 Page visible - showing foreground notification');
            if ('Notification' in window && Notification.permission === 'granted') {
              const notif = new Notification(payload.notification?.title || 'New Notification', {
                body: payload.notification?.body || payload.data?.body || '',
                icon: payload.notification?.icon || '/favicon.ico',
                tag: 'fcm-foreground-' + Date.now(),
              });
              notif.onclick = () => {
                window.focus();
                if (payload.data?.url) window.location.href = payload.data.url;
                notif.close();
              };
            }
          } else {
            console.log('🌙 Page not visible - service worker will handle notification');
          }
        });

        // Store unsubscribe function and mark as initialized
        fcmUnsubscribeRef.current = unsubscribe;
        fcmInitializedRef.current = true;
        setFcmInitialized(true);

        // Mark session as initialized
        sessionStorage.setItem(sessionKey, 'true');

        console.log('🎉 FCM initialization completed successfully');
      } catch (error) {
        console.error('❌ Failed to initialize FCM:', error);
      } finally {
        // Always clear the "initializing" flag
        fcmInitializingRef.current = false;
      }
    };

    // Only initialize if we have a userId and haven't initialized yet
    if (userId && !fcmInitializedRef.current && !fcmInitializingRef.current) {
      initializeFCM();
    }

    return () => {
      if (fcmUnsubscribeRef.current) {
        fcmUnsubscribeRef.current();
        fcmUnsubscribeRef.current = null;
      }
    };
  }, [userId, isActive]); // Only depend on userId and activation state

  // Reset FCM state when user changes (logout/login)
  useEffect(() => {
    return () => {
      // Cleanup when userId changes or component deactivates
      if (fcmUnsubscribeRef.current) {
        fcmUnsubscribeRef.current();
        fcmUnsubscribeRef.current = null;
      }
      fcmInitializingRef.current = false;
      fcmInitializedRef.current = false;
      setFcmInitialized(false);
      setPushNotificationsEnabled(false);
      console.log('🧹 FCM state reset for user change');
    };
  }, [userId, isActive]);

  useEffect(() => {
    if (!isActive) return;
    // Connect to socket.io for real-time notifications
    // Use correct URL for local and production
    const socketUrl = import.meta.env.VITE_SOCKET_URL || (window.location.hostname === 'localhost'
      ? 'http://localhost:4000'
      : window.location.origin.replace(/^http/, 'ws'));
    socketRef.current = io(socketUrl, {
      withCredentials: true,
      transports: ['websocket'],
    });
    socketRef.current.on('notification', (notification: any) => {
      setNotifications((prev) => [notification, ...prev]);
      setAllNotifications((prev) => [notification, ...prev]);
      setUnreadCount((c) => c + 1);

      // Add to popup queue - will show as modal that must be closed
      setPopupQueue((prev) => [...prev, notification]);
    });
    return () => {
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line
  }, [userId, fcmInitialized, isActive]);

  async function fetchNotifications() {
    setLoading(true);
    const res = await fetch('/api/notifications?limit=20', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setNotifications(data);
      setUnreadCount(data.filter((n: any) => n.status === 'unread').length);
    }
    setLoading(false);
  }

  async function fetchAllNotifications() {
    setLoading(true);
    const statusParam = filter !== 'all' ? `&status=${filter}` : '';
    const res = await fetch(`/api/notifications?page=${page}&limit=50${statusParam}`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      if (page === 1) {
        setAllNotifications(data);
      } else {
        setAllNotifications(prev => [...prev, ...data]);
      }
      setHasMore(data.length === 50);
    }
    setLoading(false);
  }

  async function markAsRead(id: string) {
    await fetch(`/api/notifications/${id}/read`, { method: 'PUT', credentials: 'include' });
    setNotifications(n => n.map((x: any) => x._id === id ? { ...x, status: 'read' } : x));
    setAllNotifications(n => n.map((x: any) => x._id === id ? { ...x, status: 'read' } : x));
    setUnreadCount(c => c - 1);
  }

  async function archive(id: string) {
    await fetch(`/api/notifications/${id}/archive`, { method: 'PUT', credentials: 'include' });
    setNotifications(n => n.map((x: any) => x._id === id ? { ...x, status: 'archived' } : x));
    setAllNotifications(n => n.map((x: any) => x._id === id ? { ...x, status: 'archived' } : x));
  }

  async function remove(id: string) {
    await fetch(`/api/notifications/${id}`, { method: 'DELETE', credentials: 'include' });
    setNotifications(n => n.filter((x: any) => x._id !== id));
    setAllNotifications(n => n.filter((x: any) => x._id !== id));
  }

  async function markAllAsRead() {
    await fetch('/api/notifications/mark-all-read', { method: 'PUT', credentials: 'include' });
    setNotifications(n => n.map((x: any) => ({ ...x, status: 'read' })));
    setAllNotifications(n => n.map((x: any) => ({ ...x, status: 'read' })));
    setUnreadCount(0);
  }

  async function togglePushNotifications() {
    try {
      if (pushNotificationsEnabled) {
        // Disable push notifications
        await fcmService.deleteToken();
        setPushNotificationsEnabled(false);
        console.log('Push notifications disabled');
      } else {
        // Enable push notifications
        const token = await fcmService.subscribeToPushNotifications();
        if (token) {
          setPushNotificationsEnabled(true);
          console.log('Push notifications enabled');
        }
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
    }
  }

  const filteredNotifications = allNotifications.filter(n => {
    if (filter === 'all') return true;
    return n.status === filter;
  });

  return (
    <>
      <div className="relative" ref={ref}>
        <button onClick={() => setOpen(v => !v)} className="relative p-2 rounded-full hover:bg-slate-100 focus:outline-none">
          <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">{unreadCount}</span>
          )}
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-2 min-w-[340px] max-w-[95vw] bg-white border border-slate-200 rounded-xl shadow z-50 overflow-hidden">
            <div className="sticky top-0 bg-white p-4 border-b border-slate-100 font-semibold text-slate-700 flex items-center justify-between z-10">
              <span>Recent Notifications</span>
              <div className="flex items-center gap-2">
                {fcmService.isSupported() && (
                  <button
                    className={`text-xs px-2 py-1 rounded transition ${pushNotificationsEnabled
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    onClick={togglePushNotifications}
                    title={pushNotificationsEnabled ? 'Disable push notifications' : 'Enable push notifications'}
                  >
                    {pushNotificationsEnabled ? '🔔 On' : '🔕 Off'}
                  </button>
                )}
                <button className="text-xs text-primary-600 hover:underline" onClick={fetchNotifications}>Refresh</button>
                <button className="text-xs text-primary-600 hover:underline" onClick={() => { setSidebarOpen(true); setOpen(false); }}>View All</button>
                <button className="text-xs text-primary-600 hover:underline" onClick={markAllAsRead}>Mark All as Read</button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 custom-scrollbar bg-slate-50">
              {loading ? (
                <div className="p-8 text-center text-slate-400">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-slate-400">
                  <BellIcon className="w-10 h-10 mb-2 text-slate-300" />
                  <span className="text-base font-medium">No notifications</span>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {notifications.map((n: any) => (
                    <li key={n._id} className={`flex gap-2 py-2 px-1 items-start group hover:bg-slate-100 transition ${n.status === 'unread' ? 'bg-primary-50' : ''}`}
                      onClick={() => { if (n.status === 'unread') markAsRead(n._id); if (n.link) window.location.href = n.link; }}>
                      <div className="pt-1">{getNotifIcon(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-slate-800 text-sm truncate max-w-[60%]">{n.title}</span>
                          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">{timeAgo(n.createdAt)}</span>
                        </div>
                        <div className="text-xs text-slate-600 mb-0.5 whitespace-pre-line leading-tight">{n.message}</div>
                        {n.link && <a href={n.link} className="inline-block text-xs text-primary-600 underline font-semibold hover:text-primary-800 transition">View</a>}
                      </div>
                      <div className="flex flex-row gap-1 ml-2 mt-1 opacity-80 group-hover:opacity-100">
                        {n.status !== 'archived' && <button className="p-1.5 rounded-full hover:bg-slate-200 transition" title="Archive" onClick={e => { e.stopPropagation(); archive(n._id); }}><ArchiveBoxIcon className="w-4 h-4 text-slate-400 hover:text-primary-600" /></button>}
                        <button className="p-1.5 rounded-full hover:bg-red-100 transition" title="Delete" onClick={e => { e.stopPropagation(); remove(n._id); }}><TrashIcon className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar for all notifications */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
          <div ref={sidebarRef} className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full rounded-l-2xl animate-slide-in-right">
            <div className="sticky top-0 bg-white p-5 border-b border-slate-200 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <BellIcon className="w-6 h-6 text-primary-500" /> Notifications
                {fcmService.isSupported() && (
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${pushNotificationsEnabled
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}
                    title={pushNotificationsEnabled ? 'Push notifications enabled' : 'Push notifications disabled'}
                  >
                    {pushNotificationsEnabled ? '🔔' : '🔕'}
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {fcmService.isSupported() && (
                  <button
                    className="text-xs text-primary-600 hover:underline"
                    onClick={togglePushNotifications}
                  >
                    {pushNotificationsEnabled ? 'Disable Push' : 'Enable Push'}
                  </button>
                )}
                <button
                  className="text-xs text-primary-600 hover:underline"
                  onClick={() => setShowSettings(true)}
                  title="Notification Settings"
                >
                  ⚙️ Settings
                </button>
                <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-full hover:bg-slate-100 transition">
                  <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex border-b border-slate-100 bg-slate-50 px-4 pt-2 pb-1 gap-1 text-sm">
              {[
                { key: 'all', label: 'All', icon: <BellIcon className="w-4 h-4" />, count: allNotifications.length },
                { key: 'unread', label: 'Unread', icon: <InboxIcon className="w-4 h-4" />, count: allNotifications.filter((n: any) => n.status === 'unread').length },
                { key: 'read', label: 'Read', icon: <CheckCircleIcon className="w-4 h-4" />, count: allNotifications.filter((n: any) => n.status === 'read').length },
                { key: 'archived', label: 'Archived', icon: <ArchiveBoxIcon className="w-4 h-4" />, count: allNotifications.filter((n: any) => n.status === 'archived').length }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setFilter(tab.key as any); setPage(1); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full font-medium transition-colors ${filter === tab.key
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-slate-500 hover:bg-slate-200'
                    }`}
                >
                  {tab.icon}
                  {tab.label}
                  <span className="ml-1 text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-full font-bold">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Notifications list */}
            <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
              {loading && page === 1 ? (
                <div className="p-8 text-center text-slate-400">Loading...</div>
              ) : filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-slate-400">
                  <BellIcon className="w-10 h-10 mb-2 text-slate-300" />
                  <span className="text-base font-medium">No {filter === 'all' ? '' : filter} notifications</span>
                  <span className="text-xs">You're all caught up!</span>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredNotifications.map((n: any) => (
                    <li key={n._id} className={`flex gap-2 py-2 px-1 items-start group hover:bg-slate-100 transition ${n.status === 'unread' ? 'bg-primary-50' : ''}`}
                      onClick={() => { if (n.status === 'unread') markAsRead(n._id); if (n.link) window.location.href = n.link; }}>
                      <div className="pt-1">{getNotifIcon(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-slate-800 text-sm truncate max-w-[60%]">{n.title}</span>
                          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">{timeAgo(n.createdAt)}</span>
                        </div>
                        <div className="text-xs text-slate-600 mb-0.5 whitespace-pre-line leading-tight">{n.message}</div>
                        {n.link && <a href={n.link} className="inline-block text-xs text-primary-600 underline font-semibold hover:text-primary-800 transition">View</a>}
                      </div>
                      <div className="flex flex-row gap-1 ml-2 mt-1 opacity-80 group-hover:opacity-100">
                        {n.status !== 'archived' && <button className="p-1.5 rounded-full hover:bg-slate-200 transition" title="Archive" onClick={e => { e.stopPropagation(); archive(n._id); }}><ArchiveBoxIcon className="w-4 h-4 text-slate-400 hover:text-primary-600" /></button>}
                        <button className="p-1.5 rounded-full hover:bg-red-100 transition" title="Delete" onClick={e => { e.stopPropagation(); remove(n._id); }}><TrashIcon className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Load more button */}
              {hasMore && filteredNotifications.length > 0 && (
                <div className="p-3 text-center">
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={loading}
                    className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Settings Modal */}
      <PushNotificationSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Popup Notification Modal - Must be manually closed */}
      {popupQueue.length > 0 && (
        <NotificationPopup
          notification={popupQueue[0]}
          onClose={handleClosePopup}
          onView={() => handleViewFromPopup(popupQueue[0])}
        />
      )}
    </>
  );
}